// Mock data service for analytics dashboards
export interface MockAnalyticsData {
    total_requests: number;
    blocked_requests: number;
    allowed_requests: number;
    block_rate: number;
    unique_users: number;
    unique_ips: number;
    avg_response_time: number;
    error_rate: number;
    request_timeline: Array<{
      hour: string;
      total_requests: number;
      blocked_requests: number;
      allowed_requests: number;
    }>;
    detector_stats: Array<{
      name: string;
      total_detections: number;
      block_rate: number;
      avg_confidence: number;
      last_triggered: boolean;
    }>;
    user_activity: Array<{
      ip_address: string;
      total_requests: number;
      blocked_requests: number;
      block_rate: number;
      last_seen: string;
    }>;
    top_blocking_reasons: Array<{
      reason: string;
      count: number;
    }>;
  }
  
  export interface MockGuardrailData {
    total_requests: number;
    blocked_requests: number;
    allowed_requests: number;
    block_rate: number;
    unique_users: number;
    top_blocked_categories: { [key: string]: number };
    detector_performance: { [key: string]: any };
    hourly_stats: Array<{
      hour: string;
      blocked: number;
      allowed: number;
    }>;
    user_analytics: {
      unique_users: number;
      repeat_violators: number;
      new_users_today: number;
    };
    content_analysis: {
      common_words: { [key: string]: number };
      language_distribution: { [key: string]: number };
      content_categories: { [key: string]: number };
    };
  }
  
  export interface MockAdvancedData {
    basic_metrics: {
      total_requests: number;
      blocked_requests: number;
      allowed_requests: number;
      block_rate: number;
      unique_users: number;
      unique_ips: number;
    };
    advanced_metrics: {
      requests_per_hour: number;
      block_rate_percentage: number;
      safety_effectiveness: number;
      user_engagement: {
        avg_requests_per_user: number;
        active_user_rate: number;
      };
      detector_efficiency: Record<string, {
        accuracy: number;
        volume: number;
      }>;
    };
    system_performance: {
      distributed_processing: {
        status: string;
        active_workers: number;
        queue_size: number;
        throughput: number;
        avg_processing_time: number;
      };
      gpu_acceleration: {
        active_device: string;
        available_devices: number;
        cache_hits: number;
        cache_hit_rate: number;
        memory_usage: any;
      };
      detection_performance: any;
    };
    threat_intelligence: {
      overall_threat_level: string;
      threat_distribution: Record<string, number>;
      top_categories: Array<[string, number]>;
      threat_velocity: number;
      indicators: string[];
    };
    ml_insights: any;
    predictions: any;
    executive_summary: any;
  }
  
  // Generate realistic mock data
  const generateMockAnalyticsData = (): MockAnalyticsData => {
    const totalRequests = Math.floor(Math.random() * 50000) + 10000;
    const blockedRequests = Math.floor(totalRequests * (Math.random() * 0.15 + 0.05));
    const allowedRequests = totalRequests - blockedRequests;
    const blockRate = (blockedRequests / totalRequests) * 100;
    const uniqueUsers = Math.floor(Math.random() * 5000) + 500;
    const uniqueIps = Math.floor(uniqueUsers * (Math.random() * 0.3 + 0.7));
    const avgResponseTime = Math.floor(Math.random() * 200) + 50;
    const errorRate = Math.random() * 2;
  
    // Generate timeline data
    const timeline = Array.from({ length: 24 }, (_, i) => {
      const hour = new Date();
      hour.setHours(i, 0, 0, 0);
      const hourRequests = Math.floor(Math.random() * 2000) + 500;
      const hourBlocked = Math.floor(hourRequests * (Math.random() * 0.2 + 0.05));
      return {
        hour: hour.toISOString(),
        total_requests: hourRequests,
        blocked_requests: hourBlocked,
        allowed_requests: hourRequests - hourBlocked,
      };
    });
  
    // Generate detector stats
    const detector = [
      'toxicity', 'prompt_injection', 
      'pii', 'spam', 'topic', 'fact_check'
    ];
    const detectorStats = detector.map(detector => ({
      name: detector,
      total_detections: Math.floor(Math.random() * 1000) + 100,
      block_rate: Math.random() * 30 + 10,
      avg_confidence: Math.random() * 0.3 + 0.7,
      last_triggered: Math.random() > 0.3,
    }));
  
    // Generate user activity
    const userActivity = Array.from({ length: 10 }, (_, i) => ({
      ip_address: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
      total_requests: Math.floor(Math.random() * 1000) + 50,
      blocked_requests: Math.floor(Math.random() * 200) + 10,
      block_rate: Math.random() * 40 + 5,
      last_seen: new Date(Date.now() - Math.random() * 86400000).toISOString(),
    }));
  
    // Generate blocking reasons
    const blockingReasons = [
      'Toxic content detected',
      'Personal information found',
      'Prompt injection attempt',
      'Hate speech identified',
      'False Information detected in response',
      'Privacy violation',
      'Spam content',
      'Inappropriate topic',
      'Factual inaccuracy',
    ];
    const topBlockingReasons = blockingReasons.map(reason => ({
      reason,
      count: Math.floor(Math.random() * 500) + 50,
    }));
  
    return {
      total_requests: totalRequests,
      blocked_requests: blockedRequests,
      allowed_requests: allowedRequests,
      block_rate: blockRate,
      unique_users: uniqueUsers,
      unique_ips: uniqueIps,
      avg_response_time: avgResponseTime,
      error_rate: errorRate,
      request_timeline: timeline,
      detector_stats: detectorStats,
      user_activity: userActivity,
      top_blocking_reasons: topBlockingReasons,
    };
  };
  
  const generateMockGuardrailData = (): MockGuardrailData => {
    const totalRequests = Math.floor(Math.random() * 50000) + 10000;
    const blockedRequests = Math.floor(totalRequests * (Math.random() * 0.15 + 0.05));
    const allowedRequests = totalRequests - blockedRequests;
    const blockRate = (blockedRequests / totalRequests) * 100;
    const uniqueUsers = Math.floor(Math.random() * 5000) + 500;
  
    // Generate blocked categories
    const categories = ['Toxicity', 'Sensitive Topics', 'Prompt Injection', 'Spam', 'False Information', 'PII'];
    const topBlockedCategories = categories.reduce((acc, category) => {
      acc[category] = Math.floor(Math.random() * 1000) + 100;
      return acc;
    }, {} as { [key: string]: number });
  
    // Generate detector performance
    const detectorPerformance = {
      toxicity: {
        enabled: true,
        accuracy: Math.random() * 0.2 + 0.8,
        precision: Math.random() * 0.2 + 0.8,
        recall: Math.random() * 0.2 + 0.8,
        f1_score: Math.random() * 0.2 + 0.8,
        avg_response_time: Math.floor(Math.random() * 100) + 50,
        request_count: Math.floor(Math.random() * 5000) + 1000,
      },
      topic: {
        enabled: true,
        accuracy: Math.random() * 0.2 + 0.8,
        precision: Math.random() * 0.2 + 0.8,
        recall: Math.random() * 0.2 + 0.8,
        f1_score: Math.random() * 0.2 + 0.8,
        avg_response_time: Math.floor(Math.random() * 100) + 50,
        request_count: Math.floor(Math.random() * 5000) + 1000,
      },
      prompt_injection: {
        enabled: true,
        accuracy: Math.random() * 0.2 + 0.8,
        precision: Math.random() * 0.2 + 0.8,
        recall: Math.random() * 0.2 + 0.8,
        f1_score: Math.random() * 0.2 + 0.8,
        avg_response_time: Math.floor(Math.random() * 100) + 50,
        request_count: Math.floor(Math.random() * 5000) + 1000,
      },
      hate_speech: {
        enabled: true,
        accuracy: Math.random() * 0.2 + 0.8,
        precision: Math.random() * 0.2 + 0.8,
        recall: Math.random() * 0.2 + 0.8,
        f1_score: Math.random() * 0.2 + 0.8,
        avg_response_time: Math.floor(Math.random() * 100) + 50,
        request_count: Math.floor(Math.random() * 5000) + 1000,
      },
      fact_check: {
        enabled: false,
        accuracy: Math.random() * 0.2 + 0.8,
        precision: Math.random() * 0.2 + 0.8,
        recall: Math.random() * 0.2 + 0.8,
        f1_score: Math.random() * 0.2 + 0.8,
        avg_response_time: Math.floor(Math.random() * 100) + 50,
        request_count: Math.floor(Math.random() * 5000) + 1000,
      },
      privacy: {
        enabled: true,
        accuracy: Math.random() * 0.2 + 0.8,
        precision: Math.random() * 0.2 + 0.8,
        recall: Math.random() * 0.2 + 0.8,
        f1_score: Math.random() * 0.2 + 0.8,
        avg_response_time: Math.floor(Math.random() * 100) + 50,
        request_count: Math.floor(Math.random() * 5000) + 1000,
      },
    };
  
    // Generate hourly stats
    const hourlyStats = Array.from({ length: 24 }, (_, i) => ({
      hour: `${i.toString().padStart(2, '0')}:00`,
      blocked: Math.floor(Math.random() * 100) + 10,
      allowed: Math.floor(Math.random() * 500) + 100,
    }));
  
    // Generate user analytics
    const userAnalytics = {
      unique_users: uniqueUsers,
      repeat_violators: Math.floor(uniqueUsers * 0.1),
      new_users_today: Math.floor(uniqueUsers * 0.05),
    };
  
    // Generate content analysis
    const contentAnalysis = {
      common_words: {
        'die': Math.floor(Math.random() * 500) + 100,
        'death': Math.floor(Math.random() * 400) + 80,
        'suicide': Math.floor(Math.random() * 300) + 60,
        'poison': Math.floor(Math.random() * 250) + 50,
        'kill': Math.floor(Math.random() * 200) + 40,
      },
      language_distribution: {
        'English': 85,
        'Spanish': 8,
        'French': 4,
        'German': 2,
        'Other': 1,
      },
      content_categories: {
        'Toxicity': 60,
        'Sensitive Topics': 20,
        'Prompt Injection': 15,
        'Spam': 5,
      },
    };
  
    return {
      total_requests: totalRequests,
      blocked_requests: blockedRequests,
      allowed_requests: allowedRequests,
      block_rate: blockRate,
      unique_users: uniqueUsers,
      top_blocked_categories: topBlockedCategories,
      detector_performance: detectorPerformance,
      hourly_stats: hourlyStats,
      user_analytics: userAnalytics,
      content_analysis: contentAnalysis,
    };
  };
  
  const generateMockAdvancedData = (): MockAdvancedData => {
    const totalRequests = Math.floor(Math.random() * 50000) + 10000;
    const blockedRequests = Math.floor(totalRequests * (Math.random() * 0.15 + 0.05));
    const allowedRequests = totalRequests - blockedRequests;
    const blockRate = (blockedRequests / totalRequests) * 100;
  
    return {
      basic_metrics: {
        total_requests: totalRequests,
        blocked_requests: blockedRequests,
        allowed_requests: allowedRequests,
        block_rate: blockRate,
        unique_users: Math.floor(Math.random() * 5000) + 500,
        unique_ips: Math.floor(Math.random() * 4000) + 400,
      },
      advanced_metrics: {
        requests_per_hour: Math.floor(Math.random() * 2000) + 500,
        block_rate_percentage: blockRate,
        safety_effectiveness: Math.random() * 20 + 80,
        user_engagement: {
          avg_requests_per_user: Math.random() * 10 + 5,
          active_user_rate: Math.random() * 30 + 70,
        },
        detector_efficiency: {
          toxicity: { accuracy: Math.random() * 0.2 + 0.8, volume: Math.floor(Math.random() * 5000) + 1000 },
          topic: { accuracy: Math.random() * 0.2 + 0.8, volume: Math.floor(Math.random() * 3000) + 800 },
          prompt_injection: { accuracy: Math.random() * 0.2 + 0.8, volume: Math.floor(Math.random() * 2000) + 500 },
        },
      },
      system_performance: {
        distributed_processing: {
          status: 'healthy',
          active_workers: Math.floor(Math.random() * 10) + 5,
          queue_size: Math.floor(Math.random() * 100) + 20,
          throughput: Math.floor(Math.random() * 1000) + 500,
          avg_processing_time: Math.floor(Math.random() * 100) + 50,
        },
        gpu_acceleration: {
          active_device: 'NVIDIA RTX 4090',
          available_devices: 2,
          cache_hits: Math.floor(Math.random() * 10000) + 5000,
          cache_hit_rate: Math.random() * 20 + 80,
          memory_usage: { used: 8, total: 24 },
        },
        detection_performance: {
          avg_response_time: Math.floor(Math.random() * 100) + 50,
          p95_response_time: Math.floor(Math.random() * 200) + 100,
          p99_response_time: Math.floor(Math.random() * 500) + 200,
        },
      },
      threat_intelligence: {
        overall_threat_level: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)],
        threat_distribution: {
          'low': Math.floor(Math.random() * 50) + 20,
          'medium': Math.floor(Math.random() * 30) + 10,
          'high': Math.floor(Math.random() * 20) + 5,
        },
        top_categories: [
          ['Toxicity', Math.floor(Math.random() * 1000) + 500],
          ['Sensitive Topics', Math.floor(Math.random() * 800) + 400],
          ['Prompt Injection', Math.floor(Math.random() * 600) + 300],
        ],
        threat_velocity: Math.random() * 50 + 10,
        indicators: ['Suspicious IP patterns', 'Unusual request frequency', 'Known attack signatures'],
      },
      ml_insights: {
        anomaly_detection: {
          anomalies_detected: Math.floor(Math.random() * 10) + 2,
          severity: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)],
        },
        pattern_recognition: {
          patterns: ['User behavior patterns', 'Content similarity clusters', 'Temporal patterns'],
        },
        behavioral_analysis: {
          normal_users: Math.random() * 20 + 80,
          suspicious_users: Math.random() * 20,
        },
      },
      predictions: {
        traffic_forecast: {
          predicted_requests: Math.floor(Math.random() * 10000) + 5000,
          confidence_interval: [Math.floor(Math.random() * 8000) + 4000, Math.floor(Math.random() * 12000) + 6000],
        },
        threat_forecast: {
          predicted_threats: Math.floor(Math.random() * 100) + 20,
        },
        resource_forecast: {
          cpu: Math.random() * 30 + 50,
          memory: Math.random() * 40 + 40,
          queue: Math.random() * 20 + 30,
        },
        performance_forecast: {
          response_time: Math.floor(Math.random() * 50) + 100,
          throughput: Math.floor(Math.random() * 500) + 1000,
        },
      },
      executive_summary: {
        key_metrics: {
          total_interactions: totalRequests,
          threats_blocked: blockedRequests,
          success_rate: Math.random() * 20 + 80,
          unique_users: Math.floor(Math.random() * 5000) + 500,
        },
        security_posture: {
          score: Math.floor(Math.random() * 20) + 80,
          posture: 'strong',
        },
        recommendations: [
          'Continue monitoring high-risk user patterns',
          'Consider expanding PII detection capabilities',
          'Optimize response times for peak traffic periods',
        ],
      },
    };
  };
  
  // Mock API functions that return promises to simulate real API calls
  export const mockAnalyticsAPI = {
    getAnalyticsSummary: async (timeframe: number = 24): Promise<MockAnalyticsData> => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return generateMockAnalyticsData();
    },
  
    getRealtimeStats: async (): Promise<any> => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return {
        total_requests: Math.floor(Math.random() * 50000) + 10000,
        blocked_requests: Math.floor(Math.random() * 5000) + 1000,
        allowed_requests: Math.floor(Math.random() * 45000) + 9000,
        block_rate: Math.random() * 15 + 5,
        unique_users_total: Math.floor(Math.random() * 5000) + 500,
        unique_ips_total: Math.floor(Math.random() * 4000) + 400,
        recent_requests_5min: Math.floor(Math.random() * 100) + 20,
        requests_per_minute: Math.random() * 10 + 5,
        avg_response_time: Math.floor(Math.random() * 200) + 50,
      };
    },
  
    getGuardrailData: async (hours: number = 24): Promise<MockGuardrailData> => {
      await new Promise(resolve => setTimeout(resolve, 800));
      return generateMockGuardrailData();
    },
  
    getAdvancedData: async (timeRange: number = 24): Promise<MockAdvancedData> => {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return generateMockAdvancedData();
    },
  
    getHardwareData: async (): Promise<any> => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        cpu_usage: Math.random() * 40 + 30,
        memory_usage: Math.random() * 50 + 30,
        gpu_usage: Math.random() * 60 + 20,
        disk_usage: Math.random() * 30 + 40,
        network_io: {
          sent: Math.random() * 1000000 + 500000,
          received: Math.random() * 2000000 + 1000000,
        },
        temperature: Math.random() * 30 + 40,
        model_load_times: {
          'gpt-4': Math.floor(Math.random() * 500) + 200,
          'claude-3': Math.floor(Math.random() * 400) + 150,
          'llama-2': Math.floor(Math.random() * 300) + 100,
        },
      };
    },
  }; 
